import type {
  ServerRecord,
  CredentialSummary,
  HeartbeatRecord,
  MessageLogRecord,
  GeneratorInfo,
  GeneratedClient,
  ApiErrorBody,
} from "../types";
import { getToken } from "./authToken";

// Configure via frontend/.env (VITE_API_BASE_URL) - see README. Falls back
// to the local wrangler dev port so `npm run dev` works out of the box.
const API_BASE_URL: string = import.meta.env.VITE_API_BASE_URL ?? "http://localhost:8787";

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly requestId: string;

  constructor(status: number, code: string, message: string, requestId: string) {
    super(message);
    this.status = status;
    this.code = code;
    this.requestId = requestId;
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = getToken();
  const response = await fetch(`${API_BASE_URL}${path}`, {
    ...init,
    // The admin session travels as a bearer token, not a cookie (see
    // src/auth/sessions.ts on the backend for why) - so there's no
    // `credentials: "include"` here; the token is just another header.
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...init.headers,
    },
  });

  const text = await response.text();
  const body = text.length > 0 ? JSON.parse(text) : {};

  if (!response.ok) {
    const errorBody = body as ApiErrorBody;
    throw new ApiError(
      response.status,
      errorBody.error?.code ?? "UNKNOWN_ERROR",
      errorBody.error?.message ?? "Something went wrong.",
      errorBody.error?.requestId ?? "unknown"
    );
  }

  return body as T;
}

// ------------------------------------------------------------- Auth

export function login(secret: string): Promise<{ ok: true; token: string }> {
  return request("/auth/login", { method: "POST", body: JSON.stringify({ secret }) });
}

export function logout(): Promise<{ ok: true }> {
  return request("/auth/logout", { method: "POST" });
}

export function getSession(): Promise<{ authenticated: true }> {
  return request("/auth/session");
}

// ------------------------------------------------------------- Servers

export function listServers(): Promise<{ servers: ServerRecord[] }> {
  return request("/servers");
}

export interface ServerDetail {
  server: ServerRecord;
  credential: CredentialSummary | null;
  heartbeat: HeartbeatRecord | null;
}

export function getServer(id: string): Promise<ServerDetail> {
  return request(`/servers/${id}`);
}

export function authenticateServer(id: string): Promise<{ serverId: string; credential: string }> {
  return request(`/servers/${id}/authenticate`, { method: "POST" });
}

export function invalidateAuthentication(
  id: string
): Promise<{ serverId: string; authenticationState: string }> {
  return request(`/servers/${id}/invalidate-auth`, { method: "POST" });
}

export function rotateCredential(id: string): Promise<{ serverId: string; credential: string }> {
  return request(`/servers/${id}/rotate-credential`, { method: "POST" });
}

/**
 * Sends an opaque, plain-text message to one specific server's Command
 * Bar. This client (and the Worker behind it) never parses or interprets
 * `message` in any way - it's delivered to the Roblox client verbatim.
 */
export function sendMessage(
  id: string,
  message: string
): Promise<{ messageId: string; serverId: string; queued: true }> {
  return request(`/servers/${id}/messages`, { method: "POST", body: JSON.stringify({ message }) });
}

// ------------------------------------------------------------- Logs

export function listMessageLogs(serverId: string): Promise<{ logs: MessageLogRecord[] }> {
  return request(`/servers/${serverId}/logs/messages`);
}

// ------------------------------------------------------------- Generator

export function getGeneratorInfo(): Promise<GeneratorInfo> {
  return request("/generator");
}

export interface GenerateClientRequest {
  displayName: string;
  apiBaseUrl: string;
  clientVersion: string;
}

export function generateClient(payload: GenerateClientRequest): Promise<GeneratedClient> {
  return request("/generator/generate", { method: "POST", body: JSON.stringify(payload) });
}
